import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import StudentProfileTabs from "../components/Students/StudentProfileTabs";
import StudentProfileHeader from "../components/Students/StudentProfileHeader";
import StudentOverviewTab from "../components/Students/tabs/StudentOverviewTab";
import StudentHistoryTab from "../components/Students/tabs/StudentHistoryTab";
import StudentHealthTab from "../components/Students/tabs/StudentHealthTab";
import StudentDocumentsTab from "../components/Students/tabs/StudentDocumentsTab";
import StudentFamilyTab from "../components/Students/tabs/StudentFamilyTab";
import StudentTimeline from "../components/Students/StudentTimeline";
import { buildStudentTimelineEvents } from "../utils/studentTimelineEvents";
import StudentActivityLogger from "../components/Students/StudentActivityLogger";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const GENDER_OPTIONS = [
  "",
  "Female",
  "Male",
  "Non-binary",
  "Prefer not to say",
];

const teacherOptions = [
  "Ms. Rivera",
  "Mr. Johnson",
  "Ms. Patel",
  "Ms. Chen",
  "Mr. Thompson",
];

const roomOptions = [
  "Infant Room",
  "Toddler A",
  "Toddler B",
  "Preschool A",
  "Preschool B",
  "Pre-K",
];

const gradeOptions = [
  "Infant",
  "Toddler",
  "Early Preschool",
  "Preschool",
  "Pre-K",
];

const ALLERGY_OPTIONS = [
  "Peanuts",
  "Tree Nuts",
  "Dairy",
  "Eggs",
  "Soy",
  "Wheat",
  "Fish",
  "Shellfish",
  "Sesame",
  "Latex",
  "Bee Stings",
  "Medication",
];

const SUPPORT_TAG_OPTIONS = [
  "IEP",
  "504 Plan",
  "Speech Support",
  "Occupational Therapy",
  "Physical Therapy",
  "Behavior Support",
  "Sensory Support",
  "Medical Plan",
];

const REQUIRED_HEALTH_DOCUMENTS = [
  { key: "immunizationRecord", title: "Immunization Record" },
  { key: "medicalActionPlan", title: "Medical Action Plan" },
];

const REQUIRED_COMPLIANCE_DOCUMENTS = [
  { key: "enrollmentPacket", title: "Enrollment Packet" },
  { key: "pickupAuthorizationForm", title: "Pickup Authorization Form" },
];

const StudentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const studentPhotoInputRef = useRef(null);
  const [studentActivities, setStudentActivities] = useState([]);
  const [studentReminders, setStudentReminders] = useState([]);

  useEffect(() => {
    const loadStudent = async () => {
      try {
        setLoading(true);
        setError("");
        setSaveMessage("");

        const res = await fetch(`${API_BASE}/api/students/${id}`);
        if (!res.ok) throw new Error(`Failed to load student (${res.status})`);

        const data = await res.json();

        const guardiansSource =
          Array.isArray(data?.pickups) && data.pickups.length
            ? data.pickups
            : Array.isArray(data?.guardians) && data.guardians.length
              ? data.guardians
              : [];

        const normalizedGuardians = guardiansSource.map((g, index) => ({
          ...g,
          _id: g?._id || `guardian-${index}-${Date.now()}`,
          name:
            g?.name ||
            [g?.firstName, g?.lastName].filter(Boolean).join(" ").trim() ||
            "",
          firstName:
            g?.firstName || (g?.name ? g.name.split(" ").slice(0, -1).join(" ") : ""),
          lastName:
            g?.lastName || (g?.name ? g.name.split(" ").slice(-1).join("") : ""),
          relationship: g?.relationship || "",
          phone: g?.phone || "",
          email: g?.email || "",
          address: g?.address || "",
          employer: g?.employer || "",
          workPhone: g?.workPhone || "",
          notes: g?.notes || "",
          authorized:
            g?.authorized ??
            g?.authorizedPickup ??
            g?.isAuthorizedPickup ??
            true,
          isPrimary:
            g?.isPrimary ?? g?.primary ?? g?.isPrimaryGuardian ?? g?.primaryGuardian ?? false,
          photo: g?.photo || g?.photoUrl || "",
        }));

        const primaryGuardians = normalizedGuardians.filter(
          (g) => g.contactType === "guardian"
        );

        const authorizedPickups = normalizedGuardians.filter(
          (g) => g.contactType === "pickup"
        );

        setStudent(data);
        setFormData({
          firstName: data?.firstName || "",
          lastName: data?.lastName || "",
          dob: data?.dob || "",
          gender: data?.gender || "",
          address: data?.address || "",
          room: data?.room || "",
          teacher: data?.teacher || data?.classroom || "",
          classroom: data?.classroom || data?.teacher || "",
          gradeLevel: data?.gradeLevel || data?.grade || "",
          status: data?.status || "Active",
          allergySelections: Array.isArray(data?.allergies)
            ? data.allergies.filter((a) => ALLERGY_OPTIONS.includes(a))
            : typeof data?.allergies === "string"
              ? data.allergies
                .split(",")
                .map((a) => a.trim())
                .filter((a) => ALLERGY_OPTIONS.includes(a))
              : [],
          allergyOther: Array.isArray(data?.allergies)
            ? data.allergies.filter((a) => !ALLERGY_OPTIONS.includes(a)).join(", ")
            : typeof data?.allergies === "string"
              ? data.allergies
                .split(",")
                .map((a) => a.trim())
                .filter((a) => a && !ALLERGY_OPTIONS.includes(a))
                .join(", ")
              : "",
          medications: Array.isArray(data?.medications)
            ? data.medications.join(", ")
            : data?.medications || "",
          healthRecords: data?.healthRecords || "",
          medicationAllergies: data?.medicationAllergies || "",
          disabilitySupportTags: Array.isArray(data?.disabilitySupportTags)
            ? data.disabilitySupportTags
            : [],
          supportTagsOther: data?.supportTagsOther || "",
          healthDocuments: Array.isArray(data?.healthDocuments)
            ? data.healthDocuments
            : [],
          healthRequiredDocuments: {
            immunizationRecord: data?.healthRequiredDocuments?.immunizationRecord || null,
            medicalActionPlan: data?.healthRequiredDocuments?.medicalActionPlan || null,
          },

          complianceRequiredDocuments: {
            enrollmentPacket: data?.complianceRequiredDocuments?.enrollmentPacket || null,
            pickupAuthorizationForm: data?.complianceRequiredDocuments?.pickupAuthorizationForm || null,
          },
          enrollmentDate: data?.enrollmentDate || "",
          previousSchool: data?.previousSchool || "",
          academicHistory: data?.academicHistory || "",
          emergencyContactName:
            data?.emergencyContact?.name || data?.emergency?.name || "",
          emergencyContactRelationship:
            data?.emergencyContact?.relationship ||
            data?.emergency?.relationship ||
            "",
          emergencyContactPhone:
            data?.emergencyContact?.phone ||
            data?.emergency?.phone ||
            data?.emergency?.contact ||
            "",
          emergencyContactEmail:
            data?.emergencyContact?.email || data?.emergency?.email || "",
          emergencyContactNotes:
            data?.emergencyContact?.notes || data?.emergency?.notes || "",
          documents: Array.isArray(data?.documents) ? data.documents : [],
          documentsNote: data?.documentsNote || "",
          primaryGuardians: primaryGuardians.length
            ? primaryGuardians
            : normalizedGuardians.length
              ? [{ ...normalizedGuardians[0], isPrimary: true }]
              : [],
          additionalGuardians: [],
          authorizedPickups,
          photo: data?.photo || "",
        });
      } catch (err) {
        setError(err?.message || "Failed to load student");
      } finally {
        setLoading(false);
      }
    };

    loadStudent();
  }, [id]);

  useEffect(() => {
    const fetchReminders = async () => {
      try {
        const res = await fetch(`/api/reminders?studentId=${student?._id}`);
        const data = await res.json();

        const formatted = (data || []).map((r) => ({
          id: r._id,
          type: "reminder",
          typeLabel: "Reminder",
          title: r.title || "Reminder created",
          description: r.description || "",
          timestamp: r.createdAt,
        }));

        setStudentReminders(formatted);
      } catch (err) {
        console.error("Failed to load reminders:", err);
      }
    };

    if (student?._id) {
      fetchReminders();
    }
  }, [student]);

  useEffect(() => {
    return () => {
      if (formData?.photo?.startsWith("blob:")) {
        URL.revokeObjectURL(formData.photo);
      }

      (formData?.healthDocuments || []).forEach((doc) => {
        if (doc?.fileUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(doc.fileUrl);
        }
      });

      (formData?.documents || []).forEach((doc) => {
        if (doc?.fileUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(doc.fileUrl);
        }
      });

      Object.values(formData?.healthRequiredDocuments || {}).forEach((doc) => {
        if (doc?.fileUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(doc.fileUrl);
        }
      });

      Object.values(formData?.complianceRequiredDocuments || {}).forEach((doc) => {
        if (doc?.fileUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(doc.fileUrl);
        }
      });

      (formData?.primaryGuardians || []).forEach((g) => {
        if (g?.photo?.startsWith("blob:")) {
          URL.revokeObjectURL(g.photo);
        }

      });

      (formData?.authorizedPickups || []).forEach((g) => {
        if (g?.photo?.startsWith("blob:")) {
          URL.revokeObjectURL(g.photo);
        }
      });
    };
  }, [formData]);

  const fullName =
    [formData?.firstName, formData?.lastName].filter(Boolean).join(" ").trim() ||
    [student?.firstName, student?.lastName].filter(Boolean).join(" ").trim() ||
    student?.name ||
    "Unnamed Student";

  const calculateAge = (dob) => {
    if (!dob) return "—";

    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) return "—";

    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();

    if (today.getDate() < birthDate.getDate()) {
      months -= 1;
    }

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    if (years < 0) return "—";
    if (years === 0) return `${months} month${months === 1 ? "" : "s"}`;

    return `${years} year${years === 1 ? "" : "s"}${months >= 0 ? `, ${months} month${months === 1 ? "" : "s"}` : ""}`;
  };

  const studentAge = calculateAge(formData?.dob || student?.dob);

  const missingComplianceDocs = REQUIRED_COMPLIANCE_DOCUMENTS.filter(
    (requiredDoc) =>
      !formData?.complianceRequiredDocuments?.[requiredDoc.key]?.fileUrl
  );

  const missingHealthDocs = REQUIRED_HEALTH_DOCUMENTS.filter(
    (requiredDoc) =>
      !formData?.healthRequiredDocuments?.[requiredDoc.key]?.fileUrl
  );

  const allMissingDocs = [...missingComplianceDocs, ...missingHealthDocs];

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDocumentUpload = (files) => {
    if (!files || !files.length) return;

    const newDocs = Array.from(files).map((file, index) => ({
      _id: `doc-${Date.now()}-${index}`,
      name: file.name,
      title: file.name,
      status: "Complete",
      due: "",
      uploadedAt: new Date().toISOString(),
      fileUrl: URL.createObjectURL(file),
      file,
      notes: "",
    }));

    setFormData((prev) => ({
      ...prev,
      documents: [...(prev.documents || []), ...newDocs],
    }));
  };

  const removeHealthDocument = (docId) => {
    setFormData((prev) => ({
      ...prev,
      healthDocuments: (prev.healthDocuments || []).filter(
        (doc) => doc._id !== docId
      ),
    }));
  };

  const removeComplianceDocument = (docId) => {
    setFormData((prev) => ({
      ...prev,
      documents: (prev.documents || []).filter((doc) => doc._id !== docId),
    }));
  };

  const openDocument = (fileUrl) => {
    if (!fileUrl) return;
    window.open(fileUrl, "_blank");
  };

  const downloadDocument = (fileUrl, fileName) => {
    if (!fileUrl) return;
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName || "document";
    link.click();
  };

  const printDocument = (fileUrl) => {
    if (!fileUrl) return;
    const win = window.open(fileUrl, "_blank");
    win?.print();
  };

  const handleAllergyToggle = (allergy) => {
    setFormData((prev) => {
      const current = Array.isArray(prev?.allergySelections)
        ? prev.allergySelections
        : [];
      const exists = current.includes(allergy);

      return {
        ...prev,
        allergySelections: exists
          ? current.filter((item) => item !== allergy)
          : [...current, allergy],
      };
    });
  };

  const handleSupportTagToggle = (tag) => {
    setFormData((prev) => {
      const current = Array.isArray(prev?.disabilitySupportTags)
        ? prev.disabilitySupportTags
        : [];
      const exists = current.includes(tag);

      return {
        ...prev,
        disabilitySupportTags: exists
          ? current.filter((item) => item !== tag)
          : [...current, tag],
      };
    });
  };

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handlePhotoUpload = async (field, file) => {
    if (!file) return;

    try {
      const dataUrl = await fileToDataUrl(file);

      if (field === "student") {
        setFormData((prev) => ({
          ...prev,
          photo: dataUrl,
          studentPhotoFile: file,
        }));
      }
    } catch (err) {
      console.error("Failed to process student photo:", err);
      setError("Unable to upload photo.");
    }
  };

  const handleGuardianChange = (section, guardianId, field, value) => {
    setFormData((prev) => {
      const updatedList = (prev?.[section] || []).map((g) =>
        g._id === guardianId
          ? {
            ...g,
            [field]: value,
          }
          : g
      );

      return {
        ...prev,
        [section]: updatedList,
      };
    });
  };

  const handleGuardianPhotoChange = (section, guardianId, file) => {
    if (!file) return;

    const localPreview = URL.createObjectURL(file);

    setFormData((prev) => {
      const updatedList = (prev?.[section] || []).map((g) =>
        g._id === guardianId
          ? {
            ...g,
            photo: localPreview,
            photoFile: file,
          }
          : g
      );

      return {
        ...prev,
        [section]: updatedList,
      };
    });
  };

  const addGuardian = (section) => {
    const newGuardian = {
      _id: `new-${section}-${Date.now()}`,
      name: "",
      firstName: "",
      lastName: "",
      relationship: "",
      phone: "",
      email: "",
      address: "",
      employer: "",
      workPhone: "",
      notes: "",
      contactType: section === "authorizedPickups" ? "pickup" : "guardian",
      authorized: section === "authorizedPickups",
      isPrimary: section === "primaryGuardians",
      photo: "",
    };

    setFormData((prev) => ({
      ...prev,
      [section]: [...(prev?.[section] || []), newGuardian],
    }));
  };

  const removeGuardian = async (section, guardianId) => {
    try {
      setError("");
      setSaveMessage("");

      const isNewGuardian =
        !guardianId || String(guardianId).startsWith("new-");

      if (!isNewGuardian) {
        const res = await fetch(
          `${API_BASE}/api/students/${student._id}/guardians/${guardianId}`,
          {
            method: "DELETE",
          }
        );

        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Failed to delete contact (${res.status}): ${text}`);
        }
      }

      setFormData((prev) => ({
        ...prev,
        [section]: (prev?.[section] || []).filter((g) => g._id !== guardianId),
      }));

      setSaveMessage(
        `${section === "primaryGuardians" ? "Guardian" : "Authorized pickup"} removed successfully.`
      );
    } catch (err) {
      setError(err?.message || "Failed to remove guardian");
    }
  };

  const setPrimaryGuardian = (guardianId) => {
    setFormData((prev) => ({
      ...prev,
      primaryGuardians: (prev?.primaryGuardians || []).map((g) => ({
        ...g,
        isPrimary: g._id === guardianId,
      })),
    }));
  };

  const uploadRequiredHealthDocument = (docKey, file) => {
    if (!file) return;

    const uploadedDoc = {
      _id: `health-required-${docKey}-${Date.now()}`,
      title: file.name,
      name: file.name,
      status: "Complete",
      due: "",
      uploadedAt: new Date().toISOString(),
      fileUrl: URL.createObjectURL(file),
      file,
      notes: "",
    };

    setFormData((prev) => ({
      ...prev,
      healthRequiredDocuments: {
        ...(prev.healthRequiredDocuments || {}),
        [docKey]: uploadedDoc,
      },
    }));
  };

  const uploadRequiredComplianceDocument = (docKey, file) => {
    if (!file) return;

    const uploadedDoc = {
      _id: `compliance-required-${docKey}-${Date.now()}`,
      title: file.name,
      name: file.name,
      status: "Complete",
      due: "",
      uploadedAt: new Date().toISOString(),
      fileUrl: URL.createObjectURL(file),
      file,
      notes: "",
    };

    setFormData((prev) => ({
      ...prev,
      complianceRequiredDocuments: {
        ...(prev.complianceRequiredDocuments || {}),
        [docKey]: uploadedDoc,
      },
    }));
  };

  const updateRequiredHealthDocumentField = (docKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      healthRequiredDocuments: {
        ...(prev.healthRequiredDocuments || {}),
        [docKey]: {
          ...(prev.healthRequiredDocuments?.[docKey] || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateRequiredComplianceDocumentField = (docKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      complianceRequiredDocuments: {
        ...(prev.complianceRequiredDocuments || {}),
        [docKey]: {
          ...(prev.complianceRequiredDocuments?.[docKey] || {}),
          [field]: value,
        },
      },
    }));
  };

  const removeRequiredHealthDocument = (docKey) => {
    setFormData((prev) => ({
      ...prev,
      healthRequiredDocuments: {
        ...(prev.healthRequiredDocuments || {}),
        [docKey]: null,
      },
    }));
  };

  const removeRequiredComplianceDocument = (docKey) => {
    setFormData((prev) => ({
      ...prev,
      complianceRequiredDocuments: {
        ...(prev.complianceRequiredDocuments || {}),
        [docKey]: null,
      },
    }));
  };

  const handleHealthDocumentUpload = (files) => {
    if (!files?.length) return;

    const uploaded = Array.from(files).map((file, index) => ({
      _id: `health-doc-${Date.now()}-${index}`,
      name: file.name,
      uploadedAt: new Date().toISOString(),
      fileUrl: URL.createObjectURL(file),
      file,
    }));

    setFormData((prev) => ({
      ...prev,
      healthDocuments: [...(prev?.healthDocuments || []), ...uploaded],
    }));
  };

  const handleSaveStudent = async () => {
    if (!student || !formData) return;

    try {
      setSaving(true);
      setError("");
      setSaveMessage("");

      const combinedAllergies = [
        ...(formData.allergySelections || []),
        ...(formData.allergyOther
          ? formData.allergyOther
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean)
          : []),
      ];

      const primaryGuardians = (formData.primaryGuardians || []).map((g) => ({
        ...g,
        name: g?.name || [g?.firstName, g?.lastName].filter(Boolean).join(" ").trim(),
        contactType: "guardian",
        isPrimary: !!g?.isPrimary,
        primary: !!g?.isPrimary,
        isPrimaryGuardian: !!g?.isPrimary,
        authorized: g?.authorized ?? true,
        authorizedPickup: g?.authorized ?? true,
        isAuthorizedPickup: g?.authorized ?? true,
      }));

      const authorizedPickups = (formData.authorizedPickups || []).map((g) => ({
        ...g,
        name: g?.name || [g?.firstName, g?.lastName].filter(Boolean).join(" ").trim(),
        isPrimary: false,
        primary: false,
        isPrimaryGuardian: false,
        authorized: true,
        authorizedPickup: true,
        isAuthorizedPickup: true,
      }));

      const mergedContacts = [...primaryGuardians, ...authorizedPickups].map((g) => {
        const cleanGuardian = { ...g };

        delete cleanGuardian.photoFile;
        delete cleanGuardian.isPrimaryGuardian;
        delete cleanGuardian.authorizedPickup;
        delete cleanGuardian.isAuthorizedPickup;

        if (!cleanGuardian._id || String(cleanGuardian._id).startsWith("new-")) {
          delete cleanGuardian._id;
        }

        return cleanGuardian;
      });

      const payload = {
        ...student,
        firstName: formData.firstName,
        lastName: formData.lastName,
        name: `${formData.firstName || ""} ${formData.lastName || ""}`.trim(),
        dob: formData.dob,
        medicationAllergies: formData.medicationAllergies || "",
        supportTagsOther: formData.supportTagsOther || "",
        gender: formData.gender,
        address: formData.address,
        room: formData.room,
        teacher: formData.teacher,
        classroom: formData.teacher,
        gradeLevel: formData.gradeLevel,
        status: formData.status,
        allergies: combinedAllergies,
        medications: formData.medications,
        healthRecords: formData.healthRecords,
        disabilitySupportTags: formData.disabilitySupportTags || [],

        healthDocuments: (formData.healthDocuments || []).map((doc) => {
          const cleanDoc = {
            name: doc.name || "",
            fileUrl: doc.fileUrl || "",
            uploadedAt: doc.uploadedAt || null,
            notes: doc.notes || "",
          };

          if (doc._id && !String(doc._id).startsWith("health-doc-")) {
            cleanDoc._id = doc._id;
          }

          return cleanDoc;
        }),

        healthRequiredDocuments: Object.fromEntries(
          Object.entries(formData.healthRequiredDocuments || {}).map(([key, doc]) => {
            if (!doc) return [key, null];

            const cleanDoc = {
              title: doc.title || doc.name || "Untitled Document",
              status: doc.status || "Complete",
              due: doc.due || "",
              fileUrl: doc.fileUrl || "",
              uploadedAt: doc.uploadedAt || null,
              notes: doc.notes || "",
            };

            if (doc._id && !String(doc._id).startsWith("health-required-")) {
              cleanDoc._id = doc._id;
            }

            return [key, cleanDoc];
          })
        ),

        complianceRequiredDocuments: Object.fromEntries(
          Object.entries(formData.complianceRequiredDocuments || {}).map(
            ([key, doc]) => {
              if (!doc) return [key, null];

              const cleanDoc = {
                title: doc.title || doc.name || "Untitled Document",
                status: doc.status || "Complete",
                due: doc.due || "",
                fileUrl: doc.fileUrl || "",
                uploadedAt: doc.uploadedAt || null,
                notes: doc.notes || "",
              };

              if (
                doc._id &&
                !String(doc._id).startsWith("compliance-required-")
              ) {
                cleanDoc._id = doc._id;
              }

              return [key, cleanDoc];
            }
          )
        ),

        documents: (formData.documents || []).map((doc) => {
          const cleanDoc = {
            title: doc.title || doc.name || "Untitled Document",
            status: doc.status || "Complete",
            due: doc.due || "",
            fileUrl: doc.fileUrl || "",
            uploadedAt: doc.uploadedAt || null,
            notes: doc.notes || "",
          };

          if (
            doc._id &&
            !String(doc._id).startsWith("doc-") &&
            !String(doc._id).startsWith("compliance-doc-")
          ) {
            cleanDoc._id = doc._id;
          }

          return cleanDoc;
        }),

        enrollmentDate: formData.enrollmentDate,
        previousSchool: formData.previousSchool,
        academicHistory: formData.academicHistory,
        documentsNote: formData.documentsNote,
        photo: formData.photo || student.photo || "",

        emergencyContact: {
          name: formData.emergencyContactName,
          relationship: formData.emergencyContactRelationship,
          phone: formData.emergencyContactPhone,
          email: formData.emergencyContactEmail,
          notes: formData.emergencyContactNotes,
        },

        guardians: mergedContacts,
        pickups: mergedContacts,
      };
      const res = await fetch(`${API_BASE}/api/students/${student._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to save student (${res.status}): ${text}`);
      }

      const updated = await res.json();

      setStudent(updated);

      const refreshedContacts = Array.isArray(updated?.guardians) && updated.guardians.length
        ? updated.guardians
        : Array.isArray(updated?.pickups)
          ? updated.pickups
          : [];

      const updatedGuardians = refreshedContacts.map((g, index) => ({
        ...g,
        _id: g?._id || `guardian-${index}-${Date.now()}`,
        name:
          g?.name ||
          [g?.firstName, g?.lastName].filter(Boolean).join(" ").trim() ||
          "",
        firstName:
          g?.firstName ||
          (g?.name ? g.name.split(" ").slice(0, -1).join(" ") : ""),
        lastName:
          g?.lastName || (g?.name ? g.name.split(" ").slice(-1).join("") : ""),
        relationship: g?.relationship || "",
        phone: g?.phone || "",
        email: g?.email || "",
        address: g?.address || "",
        employer: g?.employer || "",
        workPhone: g?.workPhone || "",
        notes: g?.notes || "",
        authorized:
          g?.authorized ??
          g?.authorizedPickup ??
          g?.isAuthorizedPickup ??
          true,
        isPrimary:
          g?.isPrimary ?? g?.primary ?? g?.isPrimaryGuardian ?? g?.primaryGuardian ?? index === 0,
        photo: g?.photo || g?.photoUrl || "",
      }));


      setFormData((prev) => ({
        ...prev,
        firstName: updated?.firstName || "",
        lastName: updated?.lastName || "",
        dob: updated?.dob || "",
        gender: updated?.gender || "",
        address: updated?.address || "",
        room: updated?.room || "",
        teacher: updated?.teacher || updated?.classroom || "",
        classroom: updated?.classroom || updated?.teacher || "",
        gradeLevel: updated?.gradeLevel || updated?.grade || "",
        status: updated?.status || "Active",
        allergySelections: Array.isArray(updated?.allergies)
          ? updated.allergies.filter((a) => ALLERGY_OPTIONS.includes(a))
          : [],
        allergyOther: Array.isArray(updated?.allergies)
          ? updated.allergies.filter((a) => !ALLERGY_OPTIONS.includes(a)).join(", ")
          : "",
        medications: Array.isArray(updated?.medications)
          ? updated.medications.join(", ")
          : updated?.medications || "",
        healthRecords: updated?.healthRecords || "",
        disabilitySupportTags: Array.isArray(updated?.disabilitySupportTags)
          ? updated.disabilitySupportTags
          : [],
        supportTagsOther: updated?.supportTagsOther || "",
        healthDocuments: Array.isArray(updated?.healthDocuments)
          ? updated.healthDocuments
          : [],
        enrollmentDate: updated?.enrollmentDate || "",
        previousSchool: updated?.previousSchool || "",
        academicHistory: updated?.academicHistory || "",
        documentsNote: updated?.documentsNote || "",
        emergencyContactName: updated?.emergencyContact?.name || "",
        emergencyContactRelationship: updated?.emergencyContact?.relationship || "",
        emergencyContactPhone: updated?.emergencyContact?.phone || "",
        emergencyContactEmail: updated?.emergencyContact?.email || "",
        emergencyContactNotes: updated?.emergencyContact?.notes || "",
        medicationAllergies: updated?.medicationAllergies || "",
        supportTagsOther: updated?.supportTagsOther || "",

        documents: Array.isArray(updated?.documents) ? updated.documents : [],

        healthRequiredDocuments: updated?.healthRequiredDocuments || {
          immunizationRecord: null,
          medicalActionPlan: null,
        },

        complianceRequiredDocuments: updated?.complianceRequiredDocuments || {
          enrollmentPacket: null,
          pickupAuthorizationForm: null,
        },
        photo: updated?.photo || prev?.photo || "",
        primaryGuardians: updatedGuardians.filter((g) => g.isPrimary),
        authorizedPickups: updatedGuardians.filter((g) => !g.isPrimary),
      }));

      setSaveMessage("Student profile saved.");
    } catch (err) {
      setError(err?.message || "Failed to save student profile");
    } finally {
      setSaving(false);
    }
  };

  const fieldGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  };

  const profileContentGridStyle = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 300px",
    gap: 18,
    alignItems: "start",
  };


  const cardStyle = {
    border: "1px solid var(--border, #ececec)",
    borderRadius: 10,
    padding: 12,
    background: "transparent",
    display: "grid",
    gap: 8,
  };

  const sideRailStyle = {
    border: "1px solid var(--border, #e5e7eb)",
    borderRadius: 16,
    padding: 16,
    background: "var(--card-bg, #fff)",
    display: "grid",
    gap: 12,
    alignContent: "start",
    position: "sticky",
    top: 20,
  };

  const labelStyle = {
    fontSize: ".9rem",
    fontWeight: 800,
    color: "var(--text, #222)",
  };

  const sideRailCardStyle = {
    border: "1px solid var(--border, #ececec)",
    borderRadius: 12,
    padding: 12,
    background: "transparent",
    display: "grid",
    gap: 6,
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--border, #ddd)",
    background: "#fff",
    color: "var(--text, #222)",
    outline: "none",
  };

  const dateInputStyle = {
    ...inputStyle,
    maxWidth: 220,
    paddingRight: 10,
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: 96,
    resize: "vertical",
  };

  const topButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    width: "fit-content",
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid var(--border, #ddd)",
    background: "#fff",
    color: "var(--text, #222)",
    cursor: "pointer",
    fontWeight: 700,
  };

  const actionBtnStyle = {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid var(--border, #ddd)",
    background: "#fff",
    color: "var(--text, #222)",
    cursor: "pointer",
    fontWeight: 700,
  };

  const miniActionBtnStyle = {
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid var(--border, #ddd)",
    background: "#fff",
    color: "var(--text, #222)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: ".9rem",
  };

  const photoCircleStyle = {
    width: 78,
    height: 78,
    borderRadius: "50%",
    overflow: "hidden",
    border: "1px solid var(--border, #ddd)",
    background: "rgba(92,77,125,0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    fontWeight: 800,
    cursor: "pointer",
    position: "relative",
  };

  const chipStyle = (active = false) => ({
    padding: "8px 12px",
    borderRadius: 999,
    border: `1px solid ${active ? "transparent" : "var(--border, #ddd)"}`,
    background: active ? "var(--accent, #5c4d7d)" : "#fff",
    color: active ? "#fff" : "var(--text, #222)",
    fontWeight: 700,
    cursor: "pointer",
  });

  const badgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    padding: "8px 12px",
    borderRadius: 999,
    background:
      String(formData?.status || student?.status || "").toLowerCase() === "active"
        ? "#dcfce7"
        : "#e0e7ff",
    color:
      String(formData?.status || student?.status || "").toLowerCase() === "active"
        ? "#166534"
        : "#3730a3",
    fontWeight: 800,
  };

  const getDocBadgeStyle = (docStatus) => ({
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: ".85rem",
    fontWeight: 800,
    background:
      docStatus === "Complete"
        ? "#dcfce7"
        : docStatus === "Due Soon"
          ? "#fef3c7"
          : "#fee2e2",
    color:
      docStatus === "Complete"
        ? "#166534"
        : docStatus === "Due Soon"
          ? "#92400e"
          : "#991b1b",
  });


  if (loading) {
    return <div style={{ padding: 24 }}>Loading student profile...</div>;
  }

  if (error && !student) {
    return <div style={{ padding: 24, color: "crimson" }}>{error}</div>;
  }

  if (!student || !formData) {
    return <div style={{ padding: 24 }}>Student not found.</div>;
  }


  const handleSaveStudentActivity = async (activity) => {
    setStudentActivities((prev) => [activity, ...prev]);
  };

  const messageEvents = [];
  const attendanceEvents = [];
  const reminderEvents = studentReminders;
  const incidentEvents = studentActivities.filter((item) => item.type === "incident");
  const noteEvents = studentActivities.filter(
    (item) =>
      item.type === "note" ||
      item.type === "meal" ||
      item.type === "nap" ||
      item.type === "bathroom" ||
      item.type === "reminder"
  );

  const timelineEvents = buildStudentTimelineEvents({
    formData,
    allMissingDocs,
    messages: messageEvents,
    attendance: attendanceEvents,
    reminders: reminderEvents,
    incidents: incidentEvents,
    notes: noteEvents,
  });

  const snapshotRowStyle = {
    display: "grid",
    gridTemplateColumns: "110px 1fr",
    gap: 6,
    alignItems: "center",
    fontSize: ".9rem",
  };

  return (
    <div
      style={{
        padding: "12px 20px 20px",
        display: "grid",
        gap: 14,
        alignContent: "start",
      }}
    >

      <div style={{ display: "grid", gap: 14 }}>
        <button
          type="button"
          onClick={() => navigate("/students")}
          style={topButtonStyle}
        >
          ← Back to Student Directory
        </button>

        <StudentProfileHeader
          fullName={fullName}
          formData={formData}
          student={student}
          studentAge={studentAge}
          studentPhotoInputRef={studentPhotoInputRef}
          handlePhotoUpload={handlePhotoUpload}
          handleSaveStudent={handleSaveStudent}
          saving={saving}
          actionBtnStyle={actionBtnStyle}
          badgeStyle={badgeStyle}
          photoCircleStyle={photoCircleStyle}
        />

        {(error || saveMessage) && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              border: `1px solid ${error ? "#fecaca" : "#bbf7d0"}`,
              background: error ? "#fef2f2" : "#f0fdf4",
              color: error ? "#b91c1c" : "#166534",
              fontWeight: 700,
            }}
          >
            {error || saveMessage}
          </div>
        )}
      </div>

      {/* Missing Documents Alert */}
      {allMissingDocs.length > 0 && (
        <div
          style={{
            padding: "12px 14px",
            borderRadius: 12,
            border: "1px solid #ec4899",
            background: "#fdf2f8",
            color: "#9d174d",
            fontWeight: 700,
          }}
        >
          Missing required documents:{" "}
          {allMissingDocs.map((doc) => doc.title).join(", ")}
        </div>
      )}

      {/* Student Activity Logger */}
      <StudentActivityLogger
        student={student}
        onSaveActivity={handleSaveStudentActivity}
      />

      {/* Profile Tabs */}
      <StudentProfileTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      <div style={profileContentGridStyle}>
        <div style={{ minWidth: 0 }}>
          {activeTab === "overview" && (
            <StudentOverviewTab
              formData={formData}
              handleChange={handleChange}
              fieldGridStyle={fieldGridStyle}
              cardStyle={cardStyle}
              labelStyle={labelStyle}
              inputStyle={inputStyle}
              GENDER_OPTIONS={GENDER_OPTIONS}
              teacherOptions={teacherOptions}
              roomOptions={roomOptions}
              gradeOptions={gradeOptions}
            />
          )}

          {activeTab === "health" && (
            <StudentHealthTab
              formData={formData}
              handleChange={handleChange}
              cardStyle={cardStyle}
              labelStyle={labelStyle}
              inputStyle={inputStyle}
              textareaStyle={textareaStyle}
              chipStyle={chipStyle}
              dateInputStyle={dateInputStyle}
              miniActionBtnStyle={miniActionBtnStyle}
              getDocBadgeStyle={getDocBadgeStyle}
              ALLERGY_OPTIONS={ALLERGY_OPTIONS}
              SUPPORT_TAG_OPTIONS={SUPPORT_TAG_OPTIONS}
              REQUIRED_HEALTH_DOCUMENTS={REQUIRED_HEALTH_DOCUMENTS}
              handleAllergyToggle={handleAllergyToggle}
              handleSupportTagToggle={handleSupportTagToggle}
              updateRequiredHealthDocumentField={updateRequiredHealthDocumentField}
              uploadRequiredHealthDocument={uploadRequiredHealthDocument}
              removeRequiredHealthDocument={removeRequiredHealthDocument}
              handleHealthDocumentUpload={handleHealthDocumentUpload}
              removeHealthDocument={removeHealthDocument}
              openDocument={openDocument}
              downloadDocument={downloadDocument}
              printDocument={printDocument}
            />
          )}

          {activeTab === "family" && (
            <StudentFamilyTab
              formData={formData}
              cardStyle={cardStyle}
              labelStyle={labelStyle}
              inputStyle={inputStyle}
              textareaStyle={textareaStyle}
              miniActionBtnStyle={miniActionBtnStyle}
              photoCircleStyle={photoCircleStyle}
              handleChange={handleChange}
              handleGuardianChange={handleGuardianChange}
              handleGuardianPhotoChange={handleGuardianPhotoChange}
              addGuardian={addGuardian}
              removeGuardian={removeGuardian}
              setPrimaryGuardian={setPrimaryGuardian}
            />
          )}

          {activeTab === "history" && (
            <StudentHistoryTab
              formData={formData}
              handleChange={handleChange}
              fieldGridStyle={fieldGridStyle}
              cardStyle={cardStyle}
              labelStyle={labelStyle}
              textareaStyle={textareaStyle}
            />
          )}

          {activeTab === "documents" && (
            <StudentDocumentsTab
              formData={formData}
              handleChange={handleChange}
              cardStyle={cardStyle}
              labelStyle={labelStyle}
              textareaStyle={textareaStyle}
              dateInputStyle={dateInputStyle}
              miniActionBtnStyle={miniActionBtnStyle}
              getDocBadgeStyle={getDocBadgeStyle}
              REQUIRED_COMPLIANCE_DOCUMENTS={REQUIRED_COMPLIANCE_DOCUMENTS}
              updateRequiredComplianceDocumentField={updateRequiredComplianceDocumentField}
              uploadRequiredComplianceDocument={uploadRequiredComplianceDocument}
              removeRequiredComplianceDocument={removeRequiredComplianceDocument}
              handleDocumentUpload={handleDocumentUpload}
              removeComplianceDocument={removeComplianceDocument}
              openDocument={openDocument}
              downloadDocument={downloadDocument}
              printDocument={printDocument}
            />
          )}
        </div>

        <aside style={sideRailStyle}>
          <div style={sideRailCardStyle}>
            <div style={{ fontWeight: 800, fontSize: ".98rem", marginBottom: 6 }}>
              Student Snapshot
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "110px 1fr",
                gap: 8,
                alignItems: "center",
                fontSize: ".92rem",
              }}
            >
              <div style={{ color: "var(--text-secondary, #666)", fontWeight: 700 }}>
                Status
              </div>
              <div style={{ fontWeight: 800 }}>{formData.status || "Active"}</div>

              <div style={{ color: "var(--text-secondary, #666)", fontWeight: 700 }}>
                Age
              </div>
              <div style={{ fontWeight: 800 }}>{studentAge}</div>

              <div style={{ color: "var(--text-secondary, #666)", fontWeight: 700 }}>
                Teacher
              </div>
              <div style={{ fontWeight: 800 }}>{formData.teacher || "—"}</div>

              <div style={{ color: "var(--text-secondary, #666)", fontWeight: 700 }}>
                Room
              </div>
              <div style={{ fontWeight: 800 }}>{formData.room || "—"}</div>

              <div style={{ color: "var(--text-secondary, #666)", fontWeight: 700 }}>
                Missing Docs
              </div>
              <div style={{ fontWeight: 800 }}>{allMissingDocs.length}</div>
            </div>
          </div>

          <StudentTimeline
            events={timelineEvents}
            limit={6}
            onViewAll={() => setActiveTab("history")}
          />
        </aside>
      </div>
    </div>
  );
};

export default StudentProfile;